/**
 * PulseCheckCard — premium full-area glass overlay comprehension check.
 *
 * Takes over the entire PDF viewer area with frosted glass.
 * Designed to feel like a focused, premium moment — not a quiz popup.
 *
 * Features:
 * - Full-area frosted glass (covers PDF viewer, not sidebar)
 * - Clickable progress dots with arrow navigation
 * - Score-based color feedback (0-100 → red/amber/green gradient)
 * - Smooth framer-motion transitions between concepts
 * - Three modes: type_answer, self_rate, awareness
 * - Chat locked during review (managed by parent)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  CheckCircleIcon,
  LightBulbIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { BoltIcon } from '@heroicons/react/24/solid';
import { api } from '../../config/api';
import { MarkdownRenderer } from '../MarkdownRenderer';
import { useAuth, SupportedLanguage } from '../../contexts/AuthContext';
import { LanguageToggle } from '../common/LanguageToggle';

const _MNEMO_LANG_DISPLAY: Record<string, string> = {
  hindi: 'हिन्दी',
  gujarati: 'ગુજરાતી',
  marathi: 'मराठी',
};

// ── Types ──

interface PulseConcept {
  concept_memory_id: string | null;
  concept: string;
  definition: string;
  chapter: number | null;
  importance: number;
  source_type: 'current' | 'review' | 'blind_spot';
  interaction: 'type_answer' | 'self_rate' | 'awareness';
  days_overdue: number | null;
  book_title: string;
}

interface PulseCheckCardProps {
  bookId: string;
  pagesVisited: number[];
  readingSessionId?: string | null;  // reader-mode grouping handle for translation cost rows
  onDismiss: () => void;
  onPulseActive: (active: boolean) => void;
  isDarkMode?: boolean;
}

type ConceptStatus = 'pending' | 'answered' | 'skipped';

// ── Score to color mapping ──
function getScoreColor(score: number): { bg: string; border: string; text: string; label: string; glow: string } {
  if (score >= 90) return { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-500', label: 'Nailed it', glow: 'shadow-emerald-500/20' };
  if (score >= 70) return { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-500', label: 'Solid', glow: 'shadow-emerald-500/10' };
  if (score >= 50) return { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-500', label: 'Getting there', glow: 'shadow-amber-500/10' };
  if (score >= 20) return { bg: 'bg-orange-500/10', border: 'border-orange-500/20', text: 'text-orange-500', label: 'Needs work', glow: 'shadow-orange-500/10' };
  return { bg: 'bg-red-500/10', border: 'border-red-500/20', text: 'text-red-500', label: 'Review this', glow: 'shadow-red-500/10' };
}

// ── Score ring SVG ──
function ScoreRing({ score, size = 56 }: { score: number; size?: number }) {
  const radius = (size - 6) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const colors = getScoreColor(score);

  const strokeColor = score >= 70 ? '#10b981' : score >= 50 ? '#f59e0b' : score >= 20 ? '#f97316' : '#ef4444';

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke="currentColor" strokeWidth={3} className="text-slate-200 dark:text-slate-700" />
        <motion.circle cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke={strokeColor} strokeWidth={3} strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.span
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, type: 'spring', stiffness: 200 }}
          className={`text-sm font-bold ${colors.text}`}
        >
          {score}
        </motion.span>
      </div>
    </div>
  );
}

// ── Component ──

export const PulseCheckCard: React.FC<PulseCheckCardProps> = ({
  bookId,
  pagesVisited,
  readingSessionId,
  onDismiss,
  onPulseActive,
  isDarkMode = false,
}) => {
  const { user } = useAuth();
  // EN-toggle override: null = use user.language, 'english' = peek at source.
  const [pulseLangOverride, setPulseLangOverride] = useState<SupportedLanguage | null>(null);
  const [concepts, setConcepts] = useState<PulseConcept[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isDone, setIsDone] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);
  const [conceptStatuses, setConceptStatuses] = useState<ConceptStatus[]>([]);

  // Interaction state
  const [userAnswer, setUserAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [lastScore, setLastScore] = useState<number | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [confidence, setConfidence] = useState(3);

  const currentConcept = concepts[currentIndex] || null;
  const reviewedCount = conceptStatuses.filter(s => s === 'answered').length;

  // Reset interaction state when currentIndex changes (prevents stale showFeedback)
  useEffect(() => {
    setUserAnswer('');
    setFeedback(null);
    setLastScore(null);
    setShowFeedback(false);
    setRevealed(false);
    setConfidence(3);
  }, [currentIndex]);

  // ── Lock chat ──
  useEffect(() => {
    onPulseActive(true);
    return () => onPulseActive(false);
  }, [onPulseActive]);

  // ── Fetch concepts ──
  // Deps are stabilized so we don't re-fetch on every parent render:
  //   - pagesVisited is an array; useReadingSession emits a NEW array reference
  //     on every heartbeat (progress PUT every ~3s), even when contents are
  //     identical → was triggering a /pulse/check call every 3s. Stringify
  //     pages with `.join(',')` so we only re-fetch when the set actually changes.
  //   - onDismiss is an inline arrow function on the parent (Dhyana.tsx) — new
  //     reference every render. Capture in a ref so the fetch effect doesn't
  //     depend on its identity.
  const onDismissRef = useRef(onDismiss);
  useEffect(() => { onDismissRef.current = onDismiss; }, [onDismiss]);
  const pagesKey = pagesVisited.join(',');
  useEffect(() => {
    const fetchConcepts = async () => {
      try {
        const pulseParams = new URLSearchParams({
          book_id: bookId,
          pages: pagesKey,
        });
        if (readingSessionId) pulseParams.set('reading_session_id', readingSessionId);
        if (pulseLangOverride) pulseParams.set('lang', pulseLangOverride);
        const res = await api.get(`/api/pulse/check?${pulseParams.toString()}`);
        const data = res.data.concepts || [];
        console.log(`[PulseCheck] Received ${data.length} concepts:`, data.map((c: PulseConcept) => `${c.concept} (${c.interaction}, id=${c.concept_memory_id})`));
        if (data.length === 0) { setIsEmpty(true); setIsLoading(false); return; }
        setConcepts(data);
        setConceptStatuses(new Array(data.length).fill('pending'));
        setIsLoading(false);
      } catch { onDismissRef.current(); }
    };
    fetchConcepts();
  }, [bookId, pagesKey, readingSessionId, pulseLangOverride]);

  // ── Submit typed answer ──
  const handleSubmitAnswer = async () => {
    if (!currentConcept?.concept_memory_id || !userAnswer.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.post('/api/pulse/review', {
        concept_memory_id: currentConcept.concept_memory_id,
        answer: userAnswer.trim(),
        concept: currentConcept.concept,
        definition: currentConcept.definition,
        book_id: bookId,
        confidence,
      });
      setFeedback(res.data.feedback || null);
      setLastScore(res.data.score ?? null);
      setShowFeedback(true);
      markCurrent('answered');
    } catch {
      setFeedback('Something went wrong.');
      setLastScore(0);
      setShowFeedback(true);
    } finally { setSubmitting(false); }
  };

  // ── Self-rate ──
  const handleSelfRate = async (rating: 'got_it' | 'fuzzy' | 'forgot') => {
    if (!currentConcept?.concept_memory_id) return;
    try {
      await api.post('/api/pulse/review', {
        concept_memory_id: currentConcept.concept_memory_id,
        rating, confidence,
      });
    } catch { /* silent */ }
    markCurrent('answered');
    goToNext();
  };

  // ── Status (use ref to avoid stale closures) ──
  const conceptStatusesRef = React.useRef(conceptStatuses);
  React.useEffect(() => { conceptStatusesRef.current = conceptStatuses; }, [conceptStatuses]);

  const markCurrent = (status: ConceptStatus) => {
    setConceptStatuses(prev => {
      const u = [...prev];
      u[currentIndex] = status;
      conceptStatusesRef.current = u; // sync ref immediately
      return u;
    });
  };

  // ── Navigation ──
  const resetState = () => {
    setUserAnswer(''); setFeedback(null);
    setLastScore(null); setShowFeedback(false); setRevealed(false); setConfidence(3);
  };

  const goToIndex = (i: number) => {
    if (i < 0 || i >= concepts.length) return;
    resetState(); setCurrentIndex(i);
  };

  const goToNext = useCallback(() => {
    resetState();
    const statuses = conceptStatusesRef.current; // always read latest via ref
    // Find next pending after current
    for (let i = currentIndex + 1; i < concepts.length; i++) {
      if (statuses[i] === 'pending') { setCurrentIndex(i); return; }
    }
    // Wrap around from start
    for (let i = 0; i < currentIndex; i++) {
      if (statuses[i] === 'pending') { setCurrentIndex(i); return; }
    }
    // No more pending — done
    setIsDone(true);
  }, [currentIndex, concepts.length]);

  const handleSkip = () => { markCurrent('skipped'); goToNext(); };

  // ── Dot style ──
  const getDotStyle = (i: number) => {
    if (i === currentIndex) return 'w-3 h-3 ring-2 ring-cyan-400/40 bg-cyan-500';
    if (conceptStatuses[i] === 'answered') return 'w-2 h-2 bg-emerald-500';
    if (conceptStatuses[i] === 'skipped') return 'w-2 h-2 bg-slate-500';
    return isDarkMode ? 'w-2 h-2 bg-slate-600' : 'w-2 h-2 bg-slate-300';
  };

  // ── Gradient button style ──
  const gradientBtnStyle = {
    background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
    boxShadow: '0 4px 15px rgba(6, 182, 212, 0.25)',
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="absolute inset-0 z-40 flex flex-col overflow-hidden rounded-lg"
        style={{
          backdropFilter: 'blur(24px) saturate(1.4)',
          WebkitBackdropFilter: 'blur(24px) saturate(1.4)',
          background: isDarkMode ? 'rgba(3, 7, 18, 0.88)' : 'rgba(250, 251, 252, 0.92)',
        }}
      >
        {/* ── Loading ── */}
        {isLoading && (() => {
          // EN toggle override forces English — don't claim we're translating
          // when we're actually fetching the source.
          const showingEnglishOverride = pulseLangOverride === 'english';
          const userLangDisplay = user?.language && user.language !== 'english' && !showingEnglishOverride
            ? _MNEMO_LANG_DISPLAY[user.language]
            : null;
          return (
            <div className="flex-1 flex items-center justify-center px-6">
              <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                className="flex flex-col items-center gap-4 text-center max-w-sm">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={gradientBtnStyle}>
                  <BoltIcon className="h-7 w-7 text-white animate-pulse" />
                </div>
                <div>
                  <h3 className={`text-lg font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Time for a quick refresher
                  </h3>
                  <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    {userLangDisplay
                      ? `Translating to ${userLangDisplay}...`
                      : 'Preparing Mnemo...'}
                  </p>
                </div>
                <p className={`text-xs italic mt-2 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                  Research shows self-testing boosts retention by 50% vs re-reading alone.
                </p>
              </motion.div>
            </div>
          );
        })()}

        {/* ── Empty — nothing to review yet ── */}
        {isEmpty && (
          <div className="flex-1 flex items-center justify-center px-6">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              className="flex flex-col items-center gap-4 text-center max-w-sm">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={gradientBtnStyle}>
                <BoltIcon className="h-7 w-7 text-white" />
              </div>
              <div>
                <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Nothing to review yet
                </h3>
                <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Keep reading — Mnemo will check in once you've covered a few more pages.
                </p>
              </div>
              <p className={`text-xs italic mt-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                Research shows self-testing boosts retention by 50% vs re-reading alone.
              </p>
              <button onClick={onDismiss}
                className={`mt-4 px-8 py-2.5 text-sm font-medium rounded-xl transition-all ${
                  isDarkMode ? 'bg-white/5 hover:bg-white/10 text-slate-300' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                }`}>
                Got it
              </button>
            </motion.div>
          </div>
        )}

        {/* ── Done ── */}
        {isDone && (
          <div className="flex-1 flex items-center justify-center px-6">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              className="text-center max-w-sm">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
                className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-5"
              >
                <CheckCircleIcon className="h-10 w-10 text-emerald-500" />
              </motion.div>
              <h3 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Well done
              </h3>
              <p className={`text-sm mb-8 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                {reviewedCount} of {concepts.length} concept{concepts.length !== 1 ? 's' : ''} reviewed
              </p>
              <button onClick={onDismiss}
                className="px-10 py-3 text-sm font-semibold rounded-xl text-white transition-all hover:shadow-xl"
                style={gradientBtnStyle}>
                Continue Reading
              </button>
            </motion.div>
          </div>
        )}

        {/* ── Active concept ── */}
        {!isLoading && !isDone && currentConcept && (
          <>
            {/* Top bar */}
            <div className="flex items-center justify-between px-6 pt-5 pb-2">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={gradientBtnStyle}>
                  <BoltIcon className="h-4.5 w-4.5 text-white" />
                </div>
                <div>
                  <h2 className={`text-sm font-bold tracking-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Mnemo : Your Memory, Managed
                  </h2>
                  <p className={`text-[11px] ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    {reviewedCount} of {concepts.length} reviewed
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <LanguageToggle
                  overrideLang={pulseLangOverride}
                  onToggle={setPulseLangOverride}
                  isDarkMode={isDarkMode}
                  size="xs"
                />
                <button onClick={onDismiss}
                  className={`p-2 rounded-xl transition-colors ${isDarkMode ? 'hover:bg-white/5 text-slate-500' : 'hover:bg-black/5 text-slate-400'}`}>
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Progress dots */}
            <div className="flex items-center justify-center gap-1.5 px-6 py-3">
              <button onClick={() => goToIndex(currentIndex - 1)} disabled={currentIndex === 0}
                className={`p-1 rounded-lg transition ${currentIndex === 0 ? 'opacity-20 cursor-default' : isDarkMode ? 'hover:bg-white/5' : 'hover:bg-black/5'}`}>
                <ChevronLeftIcon className={`h-3.5 w-3.5 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} />
              </button>
              {concepts.map((_, i) => (
                <button key={i} onClick={() => goToIndex(i)}
                  className={`rounded-full transition-all duration-300 ${getDotStyle(i)}`}
                />
              ))}
              <button onClick={() => goToIndex(currentIndex + 1)} disabled={currentIndex === concepts.length - 1}
                className={`p-1 rounded-lg transition ${currentIndex === concepts.length - 1 ? 'opacity-20 cursor-default' : isDarkMode ? 'hover:bg-white/5' : 'hover:bg-black/5'}`}>
                <ChevronRightIcon className={`h-3.5 w-3.5 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} />
              </button>
            </div>

            {/* Main content — centered vertically */}
            <div className="flex-1 flex items-center justify-center px-6 pb-8">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`concept-${currentIndex}`}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.2 }}
                  className="w-full max-w-md"
                >
                  {/* Motivational hook — only on first concept, before user engages */}
                  {reviewedCount === 0 && !showFeedback && (
                    <div className={`mb-5 pb-4 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-200'}`}>
                      <p className={`text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        Time for a quick refresher
                      </p>
                      <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Research shows self-testing boosts retention by <span className={`font-semibold ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>50%</span> vs re-reading alone
                      </p>
                    </div>
                  )}

                  {/* Concept name — hero */}
                  <h3 className={`text-2xl font-bold mb-6 leading-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {currentConcept.concept}
                  </h3>

                  {/* ── Feedback with score ring ── */}
                  {showFeedback && (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
                      {/* Score display */}
                      {lastScore !== null && (
                        <div className="flex items-center gap-4">
                          <ScoreRing score={lastScore} />
                          <div>
                            <p className={`text-base font-semibold ${getScoreColor(lastScore).text}`}>
                              {getScoreColor(lastScore).label}
                            </p>
                            {feedback && (
                              <div className={`text-sm mt-1 leading-relaxed ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                <MarkdownRenderer content={feedback} />
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <button onClick={() => { setShowFeedback(false); goToNext(); }}
                        className="w-full py-3 text-sm font-semibold rounded-xl text-white transition-all hover:shadow-lg"
                        style={gradientBtnStyle}>
                        {conceptStatuses.some((s, i) => i !== currentIndex && s === 'pending') ? 'Next concept' : 'Finish'}
                      </button>
                    </motion.div>
                  )}

                  {/* ── Type answer ── */}
                  {!showFeedback && currentConcept.interaction === 'type_answer' && (
                    <div className="space-y-4">
                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Explain this in your own words
                      </p>

                      <textarea
                        value={userAnswer}
                        onChange={(e) => setUserAnswer(e.target.value)}
                        placeholder="What does this concept mean to you..."
                        rows={4}
                        className={`w-full text-sm rounded-xl border px-4 py-3 resize-none transition-all focus:outline-none focus:ring-2 focus:ring-cyan-500/30 ${
                          isDarkMode
                            ? 'bg-white/5 border-white/10 text-slate-200 placeholder-slate-500 focus:border-cyan-500/40'
                            : 'bg-white border-slate-200 text-slate-800 placeholder-slate-400 focus:border-cyan-500/40'
                        }`}
                        autoFocus
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmitAnswer(); } }}
                      />

                      {/* Confidence — pill buttons */}
                      <div className="flex items-center gap-2">
                        <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Confidence</span>
                        <div className="flex gap-1">
                          {[1, 2, 3, 4, 5].map(v => (
                            <button key={v} onClick={() => setConfidence(v)}
                              className={`w-8 h-8 rounded-lg text-xs font-semibold transition-all ${
                                v <= confidence
                                  ? 'text-white shadow-sm'
                                  : isDarkMode ? 'bg-white/5 text-slate-500 border border-white/10' : 'bg-slate-50 text-slate-400 border border-slate-200'
                              }`}
                              style={v <= confidence ? { background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' } : {}}>
                              {v}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="flex gap-3 pt-1">
                        <button onClick={handleSubmitAnswer} disabled={!userAnswer.trim() || submitting}
                          className="flex-1 py-3 text-sm font-semibold rounded-xl text-white disabled:opacity-30 transition-all hover:shadow-lg"
                          style={gradientBtnStyle}>
                          {submitting ? (
                            <span className="flex items-center justify-center gap-2">
                              <ArrowPathIcon className="h-4 w-4 animate-spin" />
                              Evaluating...
                            </span>
                          ) : 'Check my answer'}
                        </button>
                        <button onClick={handleSkip}
                          className={`px-5 py-3 text-sm font-medium rounded-xl transition-colors ${
                            isDarkMode ? 'text-slate-500 hover:bg-white/5' : 'text-slate-400 hover:bg-black/5'
                          }`}>
                          Skip
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ── Self-rate ── */}
                  {!showFeedback && currentConcept.interaction === 'self_rate' && (
                    <div className="space-y-4">
                      {!revealed ? (
                        <>
                          <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            Think about what this means, then reveal the answer
                          </p>
                          <button onClick={() => setRevealed(true)}
                            className={`w-full py-3 text-sm font-medium rounded-xl border transition-all ${
                              isDarkMode
                                ? 'border-white/10 text-slate-300 hover:bg-white/5'
                                : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                            }`}>
                            Reveal Answer
                          </button>
                          <button onClick={handleSkip}
                            className={`w-full py-2 text-xs transition-colors ${isDarkMode ? 'text-slate-600 hover:text-slate-400' : 'text-slate-400 hover:text-slate-600'}`}>
                            Skip this one
                          </button>
                        </>
                      ) : (
                        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                          <div className={`p-5 rounded-xl ${
                            isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'
                          }`}>
                            <p className={`text-[10px] font-semibold uppercase tracking-widest mb-2 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                              Definition
                            </p>
                            <div className={`text-sm leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                              <MarkdownRenderer content={currentConcept.definition || `Think about what "${currentConcept.concept}" means in context — rate your confidence below.`} />
                            </div>
                          </div>

                          <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            How well did you know this?
                          </p>

                          <div className="grid grid-cols-3 gap-3">
                            <button onClick={() => handleSelfRate('got_it')}
                              className="py-3 text-sm font-semibold rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white transition-all hover:shadow-lg hover:shadow-emerald-500/20 active:scale-[0.97]">
                              Got it
                            </button>
                            <button onClick={() => handleSelfRate('fuzzy')}
                              className="py-3 text-sm font-semibold rounded-xl bg-amber-500 hover:bg-amber-600 text-white transition-all hover:shadow-lg hover:shadow-amber-500/20 active:scale-[0.97]">
                              Fuzzy
                            </button>
                            <button onClick={() => handleSelfRate('forgot')}
                              className="py-3 text-sm font-semibold rounded-xl bg-red-500 hover:bg-red-600 text-white transition-all hover:shadow-lg hover:shadow-red-500/20 active:scale-[0.97]">
                              Forgot
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </div>
                  )}

                  {/* ── Fallback: if no interaction mode matched, show as self-rate ── */}
                  {!showFeedback && !['type_answer', 'self_rate', 'awareness'].includes(currentConcept.interaction) && (
                    <div className="space-y-4">
                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        What do you know about this concept?
                      </p>
                      <button onClick={() => { markCurrent('answered'); goToNext(); }}
                        className="w-full py-3 text-sm font-semibold rounded-xl text-white transition-all hover:shadow-lg"
                        style={gradientBtnStyle}>
                        Next
                      </button>
                    </div>
                  )}

                  {/* ── Awareness ── */}
                  {!showFeedback && currentConcept.interaction === 'awareness' && (
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                          <LightBulbIcon className="h-5 w-5 text-amber-500" />
                        </div>
                        <p className={`text-sm leading-relaxed pt-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                          This is a key concept you haven't engaged with yet.
                        </p>
                      </div>

                      {currentConcept.definition && (
                        <div className={`p-5 rounded-xl ${
                          isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'
                        }`}>
                          <p className={`text-[10px] font-semibold uppercase tracking-widest mb-2 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                            Definition
                          </p>
                          <div className={`text-sm leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                            <MarkdownRenderer content={currentConcept.definition} />
                          </div>
                        </div>
                      )}

                      <button onClick={() => { markCurrent('answered'); goToNext(); }}
                        className="w-full py-3 text-sm font-semibold rounded-xl text-white transition-all hover:shadow-lg"
                        style={gradientBtnStyle}>
                        {conceptStatuses.some((s, i) => i !== currentIndex && s === 'pending') ? 'Next' : 'Done'}
                      </button>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default PulseCheckCard;
